import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, LogOut, RefreshCw, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "onboarded"];
const EQUIPMENT = ["", "Dry Van", "Reefer", "Flatbed", "Power Only", "Box Truck", "Other"];

const exportLeadsCsv = (leads) => {
  const cell = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
  const headers = ["Submitted", "First Name", "Last Name", "Company", "MC/DOT", "Equipment", "Trucks", "Email", "Phone", "Message", "Status"];
  const rows = leads.map((l) =>
    [new Date(l.created_at).toLocaleString(), l.first_name, l.last_name, l.company, l.mc_number || "", l.equipment, l.trucks ?? "", l.email, l.phone, l.message || "", l.status]
      .map(cell)
      .join(",")
  );
  const csv = [headers.map(cell).join(","), ...rows].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `purpose-haul-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const statusStyles = {
  new: "bg-white text-black",
  contacted: "bg-transparent text-white border border-white/40",
  onboarded: "bg-neutral-800 text-neutral-300 border border-white/10",
};

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [equipment, setEquipment] = useState("");
  const navigate = useNavigate();

  const token = localStorage.getItem("ph_token");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (equipment) params.equipment = equipment;
      const { data } = await axios.get(`${API}/leads`, { headers: authHeaders, params });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ph_token");
        navigate("/admin/login");
        return;
      }
      setError("Failed to load leads.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, equipment]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const updateStatus = async (id, newStatus) => {
    try {
      const { data } = await axios.patch(`${API}/leads/${id}/status`, { status: newStatus }, { headers: authHeaders });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
    } catch {
      setError("Failed to update status.");
    }
  };

  const logout = () => {
    localStorage.removeItem("ph_token");
    navigate("/admin/login");
  };

  const inputCls =
    "bg-transparent border border-white/20 px-4 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-white transition-colors duration-300";

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[70px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Purpose Haul" className="h-9 w-auto invert" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 hidden sm:inline">
              Lead Management
            </span>
            <a href="/admin/crm" data-testid="admin-nav-crm" className="font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 bg-white text-black hover:bg-neutral-300 transition-colors duration-300">
              CRM →
            </a>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors duration-300"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div>
              <h1 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl">Carrier Leads</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-2" data-testid="leads-count">
                {leads.length} submission{leads.length === 1 ? "" : "s"}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input
                  data-testid="leads-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, company, MC..."
                  className={`${inputCls} pl-9 w-64`}
                />
              </div>
              <select data-testid="leads-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} bg-[#0B0B0B]`}>
                <option value="">All Statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="leads-equipment-filter" value={equipment} onChange={(e) => setEquipment(e.target.value)} className={`${inputCls} bg-[#0B0B0B]`}>
                {EQUIPMENT.map((eq) => <option key={eq} value={eq}>{eq || "All Equipment"}</option>)}
              </select>
              <button
                data-testid="leads-export-csv-button"
                onClick={() => exportLeadsCsv(leads)}
                className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-300 hover:bg-white hover:text-black transition-colors duration-300"
              >
                <Download size={14} /> CSV
              </button>
              <button data-testid="leads-refresh-button" onClick={load} className="border border-white/20 px-4 hover:bg-white hover:text-black transition-colors duration-300" aria-label="Refresh">
                <RefreshCw size={15} />
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-400 mb-6" data-testid="leads-error">{error}</p>}

          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]" data-testid="leads-table">
              <thead>
                <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  <th className="text-left px-5 py-4">Submitted</th>
                  <th className="text-left px-5 py-4">Carrier</th>
                  <th className="text-left px-5 py-4">MC / DOT</th>
                  <th className="text-left px-5 py-4">Equipment</th>
                  <th className="text-left px-5 py-4">Trucks</th>
                  <th className="text-left px-5 py-4">Contact</th>
                  <th className="text-left px-5 py-4">Message</th>
                  <th className="text-left px-5 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="px-5 py-12 text-center text-neutral-500 font-mono text-xs uppercase tracking-[0.25em]" data-testid="leads-loading">Loading...</td></tr>
                ) : leads.length === 0 ? (
                  <tr><td colSpan={8} className="px-5 py-12 text-center text-neutral-500 font-mono text-xs uppercase tracking-[0.25em]" data-testid="leads-empty">No leads found</td></tr>
                ) : (
                  leads.map((l) => (
                    <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors duration-300" data-testid={`lead-row-${l.id}`}>
                      <td className="px-5 py-4 text-neutral-500 whitespace-nowrap font-mono text-xs">
                        {new Date(l.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-semibold">{l.first_name} {l.last_name}</div>
                        <div className="text-neutral-500 text-xs">{l.company}</div>
                      </td>
                      <td className="px-5 py-4 font-mono text-xs text-neutral-400">{l.mc_number || "—"}</td>
                      <td className="px-5 py-4 text-neutral-300">{l.equipment}</td>
                      <td className="px-5 py-4 text-neutral-400">{l.trucks || "—"}</td>
                      <td className="px-5 py-4">
                        <div className="text-xs text-neutral-300">{l.email}</div>
                        <div className="text-xs text-neutral-500">{l.phone}</div>
                      </td>
                      <td className="px-5 py-4 text-xs text-neutral-500 max-w-[220px] truncate" title={l.message || ""}>
                        {l.message || "—"}
                      </td>
                      <td className="px-5 py-4">
                        <select
                          data-testid={`lead-status-select-${l.id}`}
                          value={l.status}
                          onChange={(e) => updateStatus(l.id, e.target.value)}
                          className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 cursor-pointer ${statusStyles[l.status]}`}
                        >
                          {STATUSES.map((s) => <option key={s} value={s} className="bg-[#0B0B0B] text-white">{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
